/* // src/pages/pedidos/pedidos.css */
import React, { useMemo } from 'react'
import Button from 'react-bootstrap/Button'
import { Offcanvas, Alert, Card, Nav, Tab, Badge } from 'react-bootstrap'
import { PanelFooter, PanelHeader } from 'components/Panel'

import { usePedidoForm } from 'hooks/usePedidoForm'
import { requiereEnvio } from 'config/pedidos'
import { formatNumberForDisplay } from 'utils/numberUtils'

// Importar los componentes de tabs (excepto EstadosTab que no aplica para nuevos pedidos)
import {
	GeneralTab,
	ItemsTab,
	EntregaTab,
	FacturacionTab
} from 'components/PedidoTabs'

function NuevoPedido(props) {
	const { mostrar, guardar, cancelar } = props

	// Crear un "pedido fantasma" vacío ESTABLE (no se recrea en cada render)
	const pedidoVacio = useMemo(() => ({
		id: null,
		items: [],
		tipo: '',
		observaciones: '',
		usuario: null,
		puntoEntrega: null,
		datosFacturacion: {
			razonSocial: '',
			ruc: ''
		},
		recibido: new Date().toISOString(),
		montoTotal: 0
	}), []) // Array vacío = solo se crea una vez

	// Usar el hook como si fuera modo editar con un pedido vacío
	const pedidoForm = usePedidoForm({
		modo: 'nuevo',
		pedidoOriginal: pedidoVacio,
		onSave: (cambios) => {
			// Convertir "cambios" a "pedido completo" para POST
			const pedidoCompleto = {
				tipo: cambios.tipo || '',
				observaciones: cambios.observaciones || '',
				usuarioId: cambios.usuarioId,
				items: cambios.items || [],
				puntoEntrega: cambios.puntoEntrega,
				datosFacturacion: cambios.datosFacturacion || {
					razonSocial: '',
					ruc: ''
				},
				// Incluir totales calculados
				subtotal: cambios.subtotal,
				costoEnvio: cambios.costoEnvio,
				costoEnvioManual: cambios.costoEnvioManual || false,
				montoTotal: cambios.montoTotal
			}

			// Llamar al guardar original
			guardar(pedidoCompleto)
		},
		onCancel: cancelar
	})

	const {
		loading,
		erroresValidacion,
		activeTab,
		setActiveTab,
		datosGenerales,
		cliente,
		items,
		itemsModificados,
		puntoEntrega,
		datosFacturacion,
		costoEnvioManual,
		totales, // Totales calculados automáticamente
		handleDatosGeneralesChange,
		handleClienteChange,
		handleItemsChange,
		handlePuntoEntregaChange,
		handleDatosFacturacionChange,
		handleCostoEnvioManualChange,
		handleSave,
		handleCancel,
		hayCambios
	} = pedidoForm

	// Funciones auxiliares
	const formatearPrecio = (precio) => {
		if (!precio || precio === 0) return "₲ 0"
		return `₲ ${formatNumberForDisplay(precio.toString())}`
	}

	const calcularCantidadItems = () => {
		return totales.cantidadItems || 0
	}

	const getPasoActual = () => {
		if (!datosGenerales.tipo) return 1
		if (calcularCantidadItems() === 0) return 2
		if (requiereEnvio(datosGenerales.tipo) && !puntoEntrega?.direccion) return 3
		return 4
	}

	const esPedidoValido = () => {
		return datosGenerales.tipo &&
			calcularCantidadItems() > 0 &&
			(!requiereEnvio(datosGenerales.tipo) || (puntoEntrega?.direccion && puntoEntrega?.areaId))
	}

	return (
		<Offcanvas
			show={mostrar}
			onHide={handleCancel}
			placement="end"
			style={{ width: '80%' }}
			className="nuevo-pedido-panel"
		>
			<PanelHeader
				onClick={handleCancel}
				iconClass="fa-arrow-left"
				btnText="Atrás"
				text="Nuevo Pedido"
			/>

			<Offcanvas.Body>
				{/* Header informativo para nuevo pedido */}
				<Card className="mb-4 shadow-sm">
					<Card.Body>
						<div className="text-center">
							<h4 className="text-primary mb-2">
								<i className="fa fa-plus-circle me-2"></i>
								Crear Nuevo Pedido
							</h4>
							<p className="text-muted mb-0">
								Complete la información necesaria para crear el pedido
							</p>

							{/* Información del progreso */}
							<div className="mt-3">
								{calcularCantidadItems() > 0 ? (
									<div className="d-flex justify-content-center align-items-center gap-3 flex-wrap">
										<Badge bg="success" className="px-3 py-2">
											<i className="fa fa-shopping-cart me-1"></i>
											{calcularCantidadItems()} {calcularCantidadItems() === 1 ? 'producto' : 'productos'}
										</Badge>

										{totales.subtotal > 0 && (
											<Badge bg="info" className="px-3 py-2">
												<i className="fa fa-calculator me-1"></i>
												Subtotal: {formatearPrecio(totales.subtotal)}
											</Badge>
										)}

										{totales.costoEnvio > 0 && (
											<Badge bg="warning" className="px-3 py-2">
												<i className="fa fa-truck me-1"></i>
												Envío: {formatearPrecio(totales.costoEnvio)}
											</Badge>
										)}

										{totales.total > 0 && (
											<Badge bg="primary" className="px-3 py-2">
												<i className="fa fa-tag me-1"></i>
												<strong>Total: {formatearPrecio(totales.total)}</strong>
											</Badge>
										)}
									</div>
								) : (
									<small className="text-muted">
										<i className="fa fa-arrow-down me-1"></i>
										Siga los pasos para completar el pedido
									</small>
								)}
							</div>
						</div>
					</Card.Body>
				</Card>

				{/* Alerta inicial con pasos */}
				{!hayCambios && (
					<Alert variant="info" className="mb-4">
						<h6 className="alert-heading">
							<i className="fa fa-info-circle me-2"></i>
							Pasos para crear un pedido:
						</h6>
						<ol className="mb-0 ps-3">
							<li>Seleccione el <strong>tipo de pedido</strong> y asigne un <strong>cliente</strong></li>
							<li>Agregue los <strong>productos</strong> que el cliente desea</li>
							<li>Configure la <strong>entrega</strong> si es necesaria</li>
							<li>Complete los <strong>datos de facturación</strong> (opcional)</li>
						</ol>
					</Alert>
				)}

				{/* Indicador de progreso visual */}
				{hayCambios && (
					<Alert variant={esPedidoValido() ? "success" : "warning"} className="mb-4">
						<div className="d-flex align-items-center">
							<div className="me-3">
								<i className={`fa ${esPedidoValido() ? 'fa-check-circle' : 'fa-clock'} fa-2x`}></i>
							</div>
							<div>
								{esPedidoValido() ? (
									<>
										<strong>¡Pedido listo para crear!</strong>
										<br />
										<small>Todos los datos necesarios han sido completados.</small>
									</>
								) : (
									<>
										<strong>Paso {getPasoActual()} de 4</strong>
										<br />
										<small>
											{getPasoActual() === 1 && "Seleccione el tipo de pedido"}
											{getPasoActual() === 2 && "Agregue productos al pedido"}
											{getPasoActual() === 3 && "Configure la dirección de entrega"}
											{getPasoActual() === 4 && "Revise y confirme el pedido"}
										</small>
									</>
								)}
							</div>
						</div>
					</Alert>
				)}

				{/* Navegación por pestañas */}
				<Tab.Container activeKey={activeTab} onSelect={(key) => setActiveTab(key)}>
					<Nav variant="tabs" className="mb-4">
						<Nav.Item>
							<Nav.Link eventKey="general">
								<i className="fa fa-info-circle me-2"></i>
								General
								{!datosGenerales.tipo && (
									<i className="fa fa-exclamation-circle ms-2 text-warning" title="Requerido"></i>
								)}
								{datosGenerales.tipo && (
									<i className="fa fa-check-circle ms-2 text-success" title="Completado"></i>
								)}
							</Nav.Link>
						</Nav.Item>
						<Nav.Item>
							<Nav.Link eventKey="items">
								<i className="fa fa-shopping-cart me-2"></i>
								Productos ({calcularCantidadItems()})
								{calcularCantidadItems() === 0 && (
									<i className="fa fa-exclamation-circle ms-2 text-warning" title="Agregue al menos un producto"></i>
								)}
								{calcularCantidadItems() > 0 && (
									<i className="fa fa-check-circle ms-2 text-success" title="Productos agregados"></i>
								)}
							</Nav.Link>
						</Nav.Item>
						<Nav.Item>
							<Nav.Link
								eventKey="entrega"
								disabled={!datosGenerales.tipo || !requiereEnvio(datosGenerales.tipo)}
							>
								<i className="fa fa-map-marker-alt me-2"></i>
								Entrega
								{!requiereEnvio(datosGenerales.tipo) && (
									<small className="d-block text-muted" style={{ fontSize: '0.7rem' }}>
										{datosGenerales.tipo ? 'No aplica' : 'Seleccione tipo'}
									</small>
								)}
								{requiereEnvio(datosGenerales.tipo) && (
									<>
										{!puntoEntrega?.direccion && (
											<i className="fa fa-exclamation-circle ms-2 text-warning" title="Configure la entrega"></i>
										)}
										{puntoEntrega?.direccion && puntoEntrega?.areaId && (
											<>
												<i className="fa fa-check-circle ms-2 text-success" title="Entrega configurada"></i>
												{totales.costoEnvio > 0 && (
													<small className="d-block text-success" style={{ fontSize: '0.7rem' }}>
														{formatearPrecio(totales.costoEnvio)}
													</small>
												)}
											</>
										)}
									</>
								)}
							</Nav.Link>
						</Nav.Item>
						<Nav.Item>
							<Nav.Link eventKey="facturacion">
								<i className="fa fa-file-invoice me-2"></i>
								Facturación
								<small className="d-block text-muted" style={{ fontSize: '0.7rem' }}>
									Opcional
								</small>
							</Nav.Link>
						</Nav.Item>
					</Nav>

					<Tab.Content>
						{/* Pestaña General */}
						<Tab.Pane eventKey="general">
							<GeneralTab
								data={datosGenerales}
								onChange={handleDatosGeneralesChange}
								cliente={cliente}
								onClienteChange={handleClienteChange}
								disabled={loading}
								clienteReadOnly={false} // Nunca readonly en modo nuevo
								erroresValidacion={erroresValidacion}
							/>
						</Tab.Pane>

						{/* Pestaña Items/Productos */}
						<Tab.Pane eventKey="items">
							<ItemsTab
								items={items} // Siempre array vacío en modo nuevo
								itemsModificados={itemsModificados}
								onItemsChange={handleItemsChange}
								disabled={loading}
								showPaymentWarning={false} // Nunca hay advertencia de pago en modo nuevo
								modo="nuevo"
								totales={totales} // Pasar totales para mostrar información en tiempo real
							/>
						</Tab.Pane>

						{/* Pestaña Entrega */}
						<Tab.Pane eventKey="entrega">
							<EntregaTab
								tipoPedido={datosGenerales.tipo}
								puntoEntrega={puntoEntrega}
								onChange={handlePuntoEntregaChange}
								disabled={loading}
								showPaymentWarning={false} // Nunca hay advertencia de pago en modo nuevo
								costoEnvioManual={costoEnvioManual}
								onCostoEnvioManualChange={handleCostoEnvioManualChange}
							/>
						</Tab.Pane>

						{/* Pestaña Facturación */}
						<Tab.Pane eventKey="facturacion">
							<FacturacionTab
								datosFacturacion={datosFacturacion}
								datosOriginales={{}} // No hay datos originales en modo nuevo
								onChange={handleDatosFacturacionChange}
								disabled={loading}
								showPaymentWarning={false} // Nunca hay advertencia de pago en modo nuevo
								erroresValidacion={erroresValidacion}
							/>
						</Tab.Pane>
					</Tab.Content>
				</Tab.Container>
			</Offcanvas.Body>

			<PanelFooter>
				<div className="d-flex justify-content-between align-items-center w-100">
					<div>
						<Button
							variant="outline-secondary"
							onClick={handleCancel}
							disabled={loading}
						>
							<i className="fa fa-times me-2"></i>
							Cancelar
						</Button>
					</div>

					<div className="d-flex align-items-center">
						{hayCambios && (
							<small className="text-muted me-3">
								<i className="fa fa-edit me-1"></i>
								Paso {getPasoActual()} de 4
							</small>
						)}
						{totales.total > 0 && (
							<small className="text-primary me-3">
								<strong>Total: {formatearPrecio(totales.total)}</strong>
							</small>
						)}
						<Button
							variant="success"
							onClick={handleSave}
							disabled={loading || !esPedidoValido()}
							size="lg"
						>
							{loading ? (
								<>
									<i className="fa fa-spinner fa-spin me-2"></i>
									Creando pedido...
								</>
							) : (
								<>
									<i className="fa fa-plus me-2"></i>
									Crear Pedido
									{totales.total > 0 && (
										<span className="ms-2">({formatearPrecio(totales.total)})</span>
									)}
								</>
							)}
						</Button>
					</div>
				</div>
			</PanelFooter>
		</Offcanvas>
	)
}

export default NuevoPedido